/**
 * Simple Sales Exception Configs
 *
 * Covers simple sales exception types that share the same column layout:
 * - ex-sale-ls-not-paid-due-to-rounding
 * - voided-status-mismatch (kept for inspector route - row stays
 *   is_active=true in DB pending a "LS-internal voided drift" tagger)
 * - ex-sale-ls-xero-sync-missing
 * - ex-sale-ls-zero-balance-not-closed
 * - ex-sale-ls-closed-with-balance
 *
 * ex-sale-ls-duplicate lives in duplicateSaleExceptions.ts (grouped
 * one-row-per-group view with the Compare Duplicates dialog).
 */
import React from 'react'
import { renderSaleLsLink, renderSaleXeroLink } from './saleLinkCells'
import ClientLinkCell from '../../../components/shared/ClientLinkCell'
import { formatCurrency, formatDiscrepancy, formatBusinessDate } from '../../../utils/inspectorFormatters'
import { Chip, Tooltip } from '@mui/material'
import { withTooltip } from '../shared/muiElements'
import { buildIssuesColumn } from '../shared/issuesColumn'
import { PLATFORM_CONTEXT_COLUMNS } from '../shared/platformColumns'
import {
  composeRowSubtitle,
  datePart,
  moneyPart,
  textPart,
} from '../shared/rowSubtitle'
import type { ExceptionConfig, ExceptionColumnDef, RenderContext } from '../types'
import { statusPillChipProps } from '../../../components/shared/AppDrawer/chipPresets'
import { sharedSalesFilters, buildSalesFilterParams } from './sharedSalesFilters'

/**
 * Inspector: LS receipt + Xero invoice cells via the shared sale-link
 * renderers (no icons on this simple layout).
 */
const renderLsReceipt = (_value: any, row: Record<string, any>): React.ReactNode =>
  renderSaleLsLink(row, { showIcons: false })

const renderXeroInvoice = (_value: any, row: Record<string, any>): React.ReactNode =>
  renderSaleXeroLink(row, { showIcons: false })

/**
 * Inspector: rich ClientLinkCell
 */
const renderClient = (value: any, row: Record<string, any>, ctx: RenderContext): React.ReactNode => {
  return React.createElement(ClientLinkCell, {
    customer_id: row.customer_id,
    customer_first_name: row.customer_first_name,
    customer_last_name: row.customer_last_name,
    customer_company: row.customer_company,
    lightspeed_customer_link: row.lightspeed_customer_link,
  })
}

/**
 * Inspector: formatted date
 */
const renderDate = (value: any): React.ReactNode => {
  return formatBusinessDate(value)
}

/**
 * Inspector: discrepancy chip
 */
const renderDiscrepancy = (value: any): React.ReactNode => {
  const formatted = formatDiscrepancy(value)
  return withTooltip(
    { title: `Exact difference: ${formatted}` },
    React.createElement(Chip, statusPillChipProps(formatted, 'warning')),
  )
}

/**
 * Inspector: status chip
 */
const renderStatus = (value: any): React.ReactNode => {
  return React.createElement(
    Chip,
    statusPillChipProps(value, value === 'ONACCOUNT' ? 'warning' : 'neutral'),
  )
}

/**
 * Sort helper: client name from row fields
 */
const getClientSortValue = (row: Record<string, any>): string => {
  const parts: string[] = []
  if (row.customer_first_name) parts.push(row.customer_first_name)
  if (row.customer_last_name) parts.push(row.customer_last_name)
  if (row.customer_company) parts.push(row.customer_company)
  return parts.length > 0 ? parts.join(' ') : row.customer_id || ''
}

const columns: ExceptionColumnDef[] = [
  buildIssuesColumn(),
  ...PLATFORM_CONTEXT_COLUMNS,
  // Inspector: LS receipt with InvoiceLinkCell
  {
    key: 'invoice_number',
    label: 'Receipt #',
    contexts: ['inspector'],
    render: renderLsReceipt,
    getSortValue: (row) => row.invoice_number || '',
  },
  // Inspector: Xero invoice with InvoiceLinkCell
  {
    key: 'xero_invoice',
    label: 'Invoice',
    contexts: ['inspector'],
    render: renderXeroInvoice,
  },
  // Platform: LS Sale # (uses link config, not custom render)
  {
    key: 'invoice_number',
    label: 'LS Sale #',
    contexts: ['platform'],
    link: { field: 'lightspeed_sale_link', system: 'lightspeed' },
  },
  // Date
  {
    key: 'sale_date',
    label: 'Sale Date',
    type: 'business_date',
    render: (value, row, ctx) => ctx.variant === 'inspector' ? renderDate(value) : undefined,
    getSortValue: (row) => row.sale_date || '',
    exportFormat: 'date',
  },
  // Inspector: client with ClientLinkCell
  {
    key: 'client',
    label: 'Client',
    contexts: ['inspector'],
    render: renderClient,
    getSortValue: getClientSortValue,
  },
  // Platform: last modified date
  {
    key: 'ls_updated_at',
    label: 'Last Modified',
    type: 'date',
    contexts: ['platform'],
    exportFormat: 'date',
  },
  // Platform: client name (plain text)
  {
    key: 'customer_name',
    label: 'Client',
    contexts: ['platform'],
  },
  // Total amount
  {
    key: 'ls_total_amount',
    label: 'Total Amount',
    type: 'currency',
    align: 'right',
    render: (value, row, ctx) => ctx.variant === 'inspector' ? formatCurrency(value) : undefined,
    exportFormat: 'currency',
  },
  // Payments total
  {
    key: 'payments_total',
    label: 'Payments Total',
    type: 'currency',
    align: 'right',
    render: (value, row, ctx) => ctx.variant === 'inspector' ? formatCurrency(value) : undefined,
    exportFormat: 'currency',
  },
  // Discrepancy (inspector-only with chip)
  {
    key: 'discrepancy',
    label: 'Discrepancy',
    align: 'right',
    contexts: ['inspector'],
    render: (value) => renderDiscrepancy(value),
    getSortValue: (row) => row.discrepancy,
  },
  // Balance due (platform-only)
  {
    key: 'balance_due',
    label: 'Balance Due',
    type: 'currency',
    align: 'right',
    contexts: ['platform'],
    exportFormat: 'currency',
  },
  // Status
  {
    key: 'status',
    label: 'Sale Status',
    render: (value, row, ctx) => ctx.variant === 'inspector' ? renderStatus(value) : undefined,
  },
  // Platform: Xero reference (uses link config)
  {
    key: 'xero_reference',
    label: 'Xero Inv. #',
    contexts: ['platform'],
    link: { field: 'xero_invoice_link', system: 'xero' },
  },
  // Platform: exception type label
  {
    key: 'exception_type',
    label: 'Exception Type',
    contexts: ['platform'],
  },
]

export const simpleSalesConfig: ExceptionConfig = {
  // ex-sale-ls-duplicate is NOT here: its canonical view is the grouped
  // one-row-per-group config (duplicateSaleExceptions.ts).
  codes: [
    'ex-sale-ls-not-paid-due-to-rounding',
    'voided-status-mismatch',
    'ex-sale-ls-xero-sync-missing',
    'ex-sale-ls-zero-balance-not-closed',
    'ex-sale-ls-closed-with-balance',
  ],
  columns,
  defaultSortColumn: 'sale_date',
  defaultSortDirection: 'desc',
  complexity: 'simple',
  // Key on the canonical tag id (unique + stable per row). The backend
  // returns one canonical row per object now, so this is belt-and-braces
  // against any future multi-tag regression - never dedupe rows in JS.
  rowKeyField: 'exception_tag_id',
  getRowSubtitle: row =>
    composeRowSubtitle([
      textPart(row.customer_name) || 'Walk-in',
      datePart(row.sale_date),
      moneyPart(row.total_amount_incl_tax),
    ]),
  // Eligibility is backend-driven: ineligible codes (sync-missing,
  // duplicate) get no compare_sales payload, so their rows render no
  // Compare button.
  rowDetail: {
    type: 'compare-sales',
  },
  filters: [
    {
      key: 'search',
      type: 'search',
      label: 'Search',
      placeholder: 'Search client or sale #',
    },
    ...sharedSalesFilters,
  ],
  buildFetchParams: (urlState) => buildSalesFilterParams(urlState.filterValues),
}
